// Chat real-time per ticket via Socket.io
import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Send } from 'lucide-react'
import { Avatar } from '../../../components/ui/Avatar'
import { Skeleton } from '../../../components/ui/Skeleton'
import { useAuth } from '../../../hooks/useAuth'
import { useRealtime } from '../../../hooks/useRealtime'
import { formatDate } from '../../../lib/utils'
import api from '../../../lib/api'

interface ChatMessage {
  id: string
  userId: string
  userName: string
  content: string
  createdAt: string
}

interface TicketChatProps {
  ticketId: string
}

export function TicketChat({ ticketId }: TicketChatProps) {
  const { user } = useAuth()
  const { subscribe, emit, joinRoom, leaveRoom } = useRealtime()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  const { data: initial, isLoading } = useQuery({
    queryKey: ['ticket-chat', ticketId],
    queryFn: async () => {
      try {
        const res = await api.get<ChatMessage[]>(`/tickets/${ticketId}/messages`)
        return res.data ?? []
      } catch {
        return []
      }
    },
    enabled: Boolean(ticketId),
  })

  useEffect(() => {
    if (initial) setMessages(initial)
  }, [initial])

  useEffect(() => {
    const room = `ticket:${ticketId}`
    joinRoom(room)

    const unsub = subscribe('chat:message', (data: unknown) => {
      const msg = data as ChatMessage & { ticketId: string }
      if (msg.ticketId === ticketId) {
        setMessages((prev) => [...prev, msg])
      }
    })

    return () => {
      unsub()
      leaveRoom(room)
    }
  }, [ticketId, subscribe, joinRoom, leaveRoom])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = () => {
    if (!input.trim()) return
    emit('chat:message', { ticketId, content: input.trim() })
    setInput('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton width={32} height={32} rounded />
            <div className="flex-1 space-y-1">
              <Skeleton height={14} className="w-32" />
              <Skeleton height={12} className="w-48" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="flex h-[400px] flex-col">
      {/* Messaggi */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" role="log" aria-live="polite" aria-label="Chat ticket">
        {messages.length === 0 ? (
          <p className="text-center text-sm text-[var(--fc-foreground-muted)]">
            Nessun messaggio. Inizia la conversazione!
          </p>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.userId === user?.id
            return (
              <div
                key={msg.id}
                className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}
              >
                <Avatar name={msg.userName} size="sm" />
                <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                  <span className="text-xs text-[var(--fc-foreground-muted)]">
                    {!isOwn && `${msg.userName} · `}
                    {formatDate(msg.createdAt, 'HH:mm')}
                  </span>
                  <div
                    className={`rounded-lg px-3 py-2 text-sm ${
                      isOwn
                        ? 'bg-[var(--fc-primary)] text-white'
                        : 'bg-[var(--fc-surface-overlay)] text-[var(--fc-foreground)]'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-[var(--fc-border)] p-3 flex gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Scrivi un messaggio... (Invio per inviare)"
          rows={1}
          className="flex-1 resize-none rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-subtle)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
          aria-label="Messaggio"
        />
        <button
          onClick={handleSend}
          disabled={!input.trim()}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-[var(--fc-primary)] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--fc-primary-dark)] transition-colors"
          aria-label="Invia messaggio"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
